import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Phase 18 · Dynamic legal footer.
 * Pulled from /api/public/config.legal_footer (Super Admin editable).
 *
 * Phase 40 · Added canonical legal/FAQ navigation links so every page
 * that renders the footer exposes discoverable Terms, Privacy, Cookies
 * and FAQ links in the current language.
 */
const LegalFooter = ({ className = '' }) => {
  const { t } = useTranslation('legal');
  const { t: tContact } = useTranslation('contact');
  const { t: tRules } = useTranslation('communityRules');
  const { t: tSafety } = useTranslation('childSafety');
  const [text, setText] = useState('');

  useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/config`)
      .then((r) => r.json())
      .then((cfg) => {
        if (alive && cfg?.legal_footer) setText(cfg.legal_footer);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const links = [
    { to: '/cosa-puoi-fare',  label: 'Cosa puoi fare',   id: 'discover' },
    { to: '/terms',          label: t('terms.title'),   id: 'terms' },
    { to: '/privacy',        label: t('privacy.title'), id: 'privacy' },
    { to: '/cookies',        label: t('cookies.title'), id: 'cookies' },
    { to: '/child-safety',   label: tSafety('meta.nav_label'), id: 'child-safety' },
    { to: '/community-rules', label: tRules('title'),   id: 'community-rules' },
    { to: '/faq/users',      label: 'FAQ',              id: 'faq' },
    { to: '/contact',        label: tContact('meta.short_label'), id: 'contact' },
  ];

  return (
    <footer
      className={`flex flex-col items-center gap-2 text-center ${className}`}
      data-testid="legal-footer"
    >
      <nav
        className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-wider text-muted-foreground"
        aria-label="legal navigation"
        data-testid="legal-footer-nav"
      >
        {links.map((l, idx) => (
          <React.Fragment key={l.id}>
            {idx > 0 && <span aria-hidden className="opacity-40">·</span>}
            <Link
              to={l.to}
              className="transition hover:text-amber-200"
              data-testid={`legal-footer-link-${l.id}`}
            >
              {l.label}
            </Link>
          </React.Fragment>
        ))}
      </nav>
      {text && (
        <p
          className="text-[10px] uppercase tracking-wider text-muted-foreground"
          data-testid="legal-footer-text"
        >
          {text}
        </p>
      )}
    </footer>
  );
};

export default LegalFooter;
